import { FormEvent, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Alert, Link, Stack, TextField, Typography } from '@mui/material';
import { ApiError, requestPasswordResetRequest } from '@/auth/api';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import { SectionCard } from '@/components/SectionCard';

/**
 * "Forgot my password" (CTA-2). The answer is the same whether the mail has an account or not,
 * so the page never says which: it only says what to look for in the inbox.
 */
export function CustomerForgotPasswordPage() {
  const location = useLocation();
  // The login page passes along the email the buyer had already typed.
  const stateEmail =
    typeof location.state === 'object' &&
    location.state &&
    'email' in location.state &&
    typeof location.state.email === 'string'
      ? location.state.email
      : '';
  const [email, setEmail] = useState(stateEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Ingresá un email válido.');
      return;
    }

    setIsSubmitting(true);
    try {
      await requestPasswordResetRequest(email.trim());
      setSent(true);
    } catch (error) {
      setErrorMessage(
        error instanceof ApiError && error.status === 429
          ? 'Ya pediste un enlace hace poco. Esperá unos minutos antes de pedir otro.'
          : 'No pudimos enviar el enlace. Volvé a intentar en unos minutos.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <NarrowPage maxWidth={600}>
      <PageHeader
        eyebrow="TU CUENTA DE PHONETEC"
        title="Recuperá tu contraseña"
        description="Te enviamos un enlace a tu email para que elijas una contraseña nueva."
      />
      <SectionCard title={sent ? 'Revisá tu email' : '¿Cuál es el email de tu cuenta?'}>
        <Stack component="form" spacing={2} onSubmit={handleSubmit} noValidate>
          {sent ? (
            <Alert severity="success">
              Si {email.trim()} tiene una cuenta en PhoneTec, te enviamos un enlace para elegir una
              contraseña nueva. Vence en 1 hora. Si no lo ves, revisá spam o correo no deseado.
            </Alert>
          ) : null}
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setEmailError(null);
            }}
            error={Boolean(emailError)}
            helperText={emailError}
            required
            fullWidth
          />
          <PrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Enviando...' : sent ? 'Enviar otro enlace' : 'Enviar enlace'}
          </PrimaryButton>
          <Typography variant="body2" color="text.secondary">
            ¿Te acordaste?{' '}
            <Link component={RouterLink} to="/login" underline="hover">
              Volvé a iniciar sesión
            </Link>
          </Typography>
        </Stack>
      </SectionCard>
    </NarrowPage>
  );
}
